import {
  PlanFamily,
  Discount,
  OrderContext,
  AcquisitionChannel,
  BillingChannel,
  Subscription,
} from '@localstack/types';
import moment from 'moment/moment';

type HintsIn = {
  isSelfServe: boolean;
  isStripeBilling: boolean;
  isEditing: boolean;
  isSalesLed: boolean;
  planFamily: PlanFamily;
  planIntervalMonths: number;
  hasSeatsProduct: boolean;
  selectedDiscount?: Optional<Discount>;
  context: Pick<OrderContext, 'interval' | 'volumes' | 'proration_behavior' | 'custom_renewal_date'>;
};

/**
 * Build contextual helper messages for the subscription constructor UI.
 *
 * Produces two arrays:
 * - `info`: neutral/positive guidance (e.g., interval overrides, proration behavior, read-only discount)
 * - `warn`: cautions where user input will be ignored or may not behave as expected
 *
 * The hints are derived from the selected plan family/interval and the current order
 * context (interval override, custom volumes, proration behavior, custom renewal date),
 * as well as flow flags (self-serve vs. sales-led, Stripe billing vs. invoice, editing state).
 * This function is **pure**; it has no side effects.
 *
 * @param params - Inputs used to compute hints.
 * @param params.isSelfServe - True when the acquisition channel is self-serve.
 * @param params.isStripeBilling - True when the billing method is Stripe (vs. invoice).
 * @param params.isEditing - True when updating an existing subscription (not creating).
 * @param params.isSalesLed - True when the acquisition channel is sales-led.
 * @param params.planFamily - Selected plan family (e.g., `PlanFamily.HOBBY_PLANS`).
 * @param params.planIntervalMonths - Plan’s default billing interval in months.
 * @param params.hasSeatsProduct - Whether at least one Seats-type product is selected.
 * @param params.selectedDiscount - Currently selected discount (read-only during edits).
 * @param params.context - Order context affecting behavior.
 * @param params.context.interval - Optional interval override in months.
 * @param params.context.volumes - Optional custom product volumes; resets on plan change.
 * @param params.context.proration_behavior - How Stripe should apply proration.
 * @param params.context.custom_renewal_date - Optional custom renewal date anchor.
 *
 * @returns An object with `info` and `warn` message arrays for display.
 *
 * @remarks
 * - If self-serve + Stripe and no Seats product is selected, an info hint prompts for a Seats product.
 * - If `context.interval` differs from `planIntervalMonths`, an info hint notes the override.
 * - Custom volumes presence is surfaced as an info hint (they reset on plan change).
 * - During edits, hints explain anchor behavior and preserve an existing discount.
 * - Proration behavior (if not `none`) is surfaced as an info hint.
 * - For Hobby plans, a non-commercial-use reminder is added.
 * - A custom renewal date is **ignored** unless acquisition is Sales-led **and** billing is Invoice; this yields a warning.
 */
export const computeConstructorHints = ({
  isSelfServe,
  isStripeBilling,
  isEditing,
  isSalesLed,
  planFamily,
  planIntervalMonths,
  hasSeatsProduct,
  selectedDiscount,
  context,
}: HintsIn): { info: string[]; warn: string[] } => {
  const info: string[] = [];
  const warn: string[] = [];

  if (isSelfServe && isStripeBilling && !hasSeatsProduct) {
    info.push('Select at least one [Seats] product.');
  }

  info.push('You can add only one product per type.');

  const effectiveInterval = context.interval ?? planIntervalMonths;
  if (effectiveInterval !== planIntervalMonths) {
    info.push(`Payments interval overridden to ${effectiveInterval} mo (plan default: ${planIntervalMonths} mo).`);
  }

  if (Object.keys(context.volumes ?? {}).length > 0) {
    info.push('Custom volumes are active and will reset if you change the plan.');
  }

  if (isEditing) {
    info.push('On updates: “unchanged” keeps the current billing anchor; “now” re-anchors immediately with proration.');
  }

  if (context.proration_behavior && context.proration_behavior !== 'none') {
    info.push(
      `Proration behavior: ${String(context.proration_behavior).replace('_', ' ')} (controls how credits/charges are applied).`,
    );
  }

  if (isEditing && selectedDiscount) {
    info.push('Discount is read-only here and will be preserved on submit.');
  }

  if (!isEditing) {
    info.push('Leave Billing Cycle Anchor empty to start on the creation date.');
  }

  if (planFamily === PlanFamily.HOBBY_PLANS) {
    info.push('Hobby plans are for non-commercial use only.');
  }

  if (context.custom_renewal_date && (!isSalesLed || isStripeBilling)) {
    warn.push('Custom Renewal Date is ignored unless Acquisition is Sales-led and Billing is Invoice.');
  }

  return { info, warn };
};

/**
 * Select the “Custom Payment [Forever 100%]” (or equivalent) discount from a list.
 *
 * Prefers a discount that:
 *  1) has a name matching `/custom\s*payment/i`,
 *  2) has `duration_months === -1` (forever),
 *  3) and is effectively free (either `discount_percentage === 100` or `discount_flat === 0`).
 *
 * If no name-matching item is found, returns any discount that is forever and free by the same criteria.
 *
 * @param list - Candidate discounts to search (optional).
 * @returns The matching "forever free" discount if found; otherwise `undefined`.
 *
 * @remarks
 * - `duration_months === -1` denotes a perpetual (“forever”) discount.
 * - “Free” is detected via either a 100% percentage or a flat 0 amount.
 * - Name matching is a soft preference; absence of the “Custom Payment” name still allows a match.
 *
 * @example
 * const d = pickCustomForever(discounts);
 * if (d) {
 *   // enforce custom-forever discount
 * }
 */
export const pickCustomForever = (list?: Discount[]): Discount | undefined =>
  (list ?? []).find(
    (d) =>
      /custom\s*payment/i.test(d.name ?? '') &&
      d.duration_months === -1 &&
      ((d.discount_percentage ?? 0) === 100 || (d.discount_flat ?? -1) === 0),
  ) || (list ?? []).find((d) => d.duration_months === -1 && (d.discount_percentage === 100 || d.discount_flat === 0));

const getEffectiveAcquisitionChannel = (
  overrides: Partial<OrderContext>,
  subscription?: Subscription | null,
): AcquisitionChannel => overrides.acquisition_channel ?? subscription?.acquisition_channel ?? AcquisitionChannel.SALES;

const getEffectiveBillingChannel = (
  overrides: Partial<OrderContext>,
  subscription?: Subscription | null,
): BillingChannel => overrides.billing_channel ?? subscription?.billing_channel ?? BillingChannel.INVOICE;

const getIntervalForDefault = (
  overrides: Partial<OrderContext>,
  fallbackIntervalMonths: number,
  subscription?: Subscription | null,
): number => overrides.interval ?? subscription?.interval_months ?? fallbackIntervalMonths;

const computeDefaultCustomRenewalDate = (
  shouldHaveCustomRenewal?: boolean,
  intervalMonths?: number,
): number | undefined => {
  if (!shouldHaveCustomRenewal || !intervalMonths) return undefined;
  const base = moment();
  return base.add(intervalMonths, 'months').startOf('day').unix();
};

export const getRenewalDate = (
  overrides: Partial<OrderContext>,
  fallbackIntervalMonths: number,
  subscription?: Subscription | null,
  adminMode?: boolean,
): number | undefined => {
  const effectiveAcq = getEffectiveAcquisitionChannel(overrides, subscription);
  const effectiveBilling = getEffectiveBillingChannel(overrides, subscription);
  const intervalForDefault = getIntervalForDefault(overrides, fallbackIntervalMonths, subscription);

  const isAdminCreate = adminMode && !subscription;
  const shouldHaveCustomRenewal =
    isAdminCreate && effectiveAcq === AcquisitionChannel.SALES && effectiveBilling !== BillingChannel.STRIPE;

  const defaultCustomRenewalDate = computeDefaultCustomRenewalDate(shouldHaveCustomRenewal, intervalForDefault);

  // Priority: user override > subscription value > computed default
  return overrides.custom_renewal_date ?? subscription?.custom_renewal_date ?? defaultCustomRenewalDate;
};
